/**
 * editorTools — SvgFigureEditor 의 도구 정의와 요소 생성·조회(React 없음, DOM 만).
 *
 * 그리기 도구는 **svgPolicy 허용 요소·속성만** 만든다(계약 §3-3 · §6-1): line/rect/ellipse/circle/polygon/polyline/path/text,
 * 화살표는 defs 안 marker(id `f-arrow`) + `marker-end="url(#f-arrow)"`. 새 요소 id 는 `f1`, `f2`… 로 기존 id 와 겹치지 않게.
 * 색은 currentColor 와 CSS 이름 색만 쓴다(hex 리터럴 없음 — 저장본은 <img> 로 보이므로 CSS 변수도 못 쓴다).
 */
import { SVG_NS } from './svgPolicy';
import { fmt } from './editorTransform';

export const TOOLS = Object.freeze([
  { id: 'select', label: '선택', hint: '클릭 선택 · 드래그 이동 · Shift 로 여러 개' },
  { id: 'line', label: '선', hint: '드래그 · Shift 면 45° 단위' },
  { id: 'arrow', label: '화살표', hint: '드래그 — 끝에 화살촉' },
  { id: 'rect', label: '직사각형', hint: '드래그 · Shift 면 정사각형' },
  { id: 'ellipse', label: '원/타원', hint: '드래그 · Shift 면 원' },
  { id: 'polygon', label: '다각형', hint: '클릭으로 꼭짓점 · 더블클릭으로 끝' },
  { id: 'polyline', label: '꺾은선', hint: '클릭으로 꼭짓점 · 더블클릭으로 끝' },
  { id: 'freehand', label: '자유곡선', hint: '드래그' },
  { id: 'text', label: '글자', hint: '클릭한 자리에 글자' },
]);
/** 드래그 한 번으로 끝나는 도구 / 클릭을 모아 더블클릭으로 끝내는 도구. */
export const DRAG_TOOLS = new Set(['line', 'arrow', 'rect', 'ellipse', 'freehand']);
export const CLICK_TOOLS = new Set(['polygon', 'polyline']);
/** 캔버스·목록에서 고를 수 있는 요소. tspan 은 text 로, defs 안은 제외. */
export const SELECTABLE = new Set(['g', 'rect', 'path', 'line', 'ellipse', 'circle', 'polyline', 'polygon', 'text']);
export const ARROW_MARKER_ID = 'f-arrow';
export const GRID_DIVISIONS = 50;
export const TEXT_SIZE_RATIO = 0.04;

export const DASH_PRESETS = Object.freeze([
  { id: 'solid', label: '실선', pattern: null },
  { id: 'dashed', label: '파선', pattern: [4, 2] },
  { id: 'dotted', label: '점선', pattern: [1, 2] },
  { id: 'dashdot', label: '일점쇄선', pattern: [6, 2, 1, 2] },
]);
export const LINECAPS = Object.freeze([['butt', '평평'], ['round', '둥글게'], ['square', '네모']]);
export const ANCHORS = Object.freeze([['start', '왼쪽 정렬'], ['middle', '가운데 정렬'], ['end', '오른쪽 정렬']]);
/** 색 선택지 — 값은 SVG 에 그대로 들어가는 이름 색(검정은 currentColor = 서버 규칙과 같음). 견본 색은 svgEditor.css 클래스. */
export const SWATCHES = Object.freeze([
  { value: 'currentColor', label: '검정', cls: 'svg-editor-swatch--current' },
  { value: 'gray', label: '회색', cls: 'svg-editor-swatch--gray' },
  { value: 'red', label: '빨강', cls: 'svg-editor-swatch--red' },
  { value: 'blue', label: '파랑', cls: 'svg-editor-swatch--blue' },
  { value: 'green', label: '초록', cls: 'svg-editor-swatch--green' },
  { value: 'orange', label: '주황', cls: 'svg-editor-swatch--orange' },
  { value: 'white', label: '흰색', cls: 'svg-editor-swatch--white' },
  { value: 'none', label: '없음', cls: 'svg-editor-swatch--none' },
]);

const ELEMENT_NODE = 1;

/** root 와 모든 자손 요소(문서 순서). childNodes 순회 — svgPolicy 와 같은 이유(jsdom HTMLCollection 은 O(n²)). */
export function descendants(root) {
  const out = [];
  const stack = [root];
  while (stack.length) {
    const el = stack.pop();
    out.push(el);
    const kids = el.childNodes;
    for (let i = kids.length - 1; i >= 0; i -= 1) if (kids[i].nodeType === ELEMENT_NODE) stack.push(kids[i]);
  }
  return out;
}

export function elementChildren(el) {
  return Array.from(el.childNodes).filter((n) => n.nodeType === ELEMENT_NODE);
}

export function viewBoxOf(root) {
  const box = (root.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number);
  return box.length === 4 && box.every(Number.isFinite) && box[2] > 0 && box[3] > 0 ? box : [0, 0, 100, 100];
}

export const gridOf = (root) => viewBoxOf(root)[2] / GRID_DIVISIONS;

/** 목록에 보일 요소 — 루트 아래를 그룹 안까지 문서 순서로(defs 제외, text 의 tspan 은 안 보임). */
export function listSelectable(root) {
  const out = [];
  const visit = (parent, depth) => {
    for (const el of elementChildren(parent)) {
      if (el.namespaceURI !== SVG_NS || !SELECTABLE.has(el.localName)) continue;
      out.push({ el, depth });
      if (el.localName === 'g') visit(el, depth + 1);
    }
  };
  visit(root, 0);
  return out;
}

/** 이벤트 target 에서 고를 수 있는 가장 안쪽 요소로(tspan → text). 루트·바깥·defs 안이면 null. */
export function selectableTarget(root, target) {
  let el = target;
  while (el && el !== root && el.nodeType === ELEMENT_NODE) {
    if (el.namespaceURI === SVG_NS && SELECTABLE.has(el.localName)) {
      let p = el.parentNode;
      while (p && p !== root) {
        if (p.localName === 'defs') return null;
        p = p.parentNode;
      }
      return p === root ? el : null;
    }
    el = el.parentNode;
  }
  return null;
}

export function usedIds(root) {
  const ids = new Set();
  for (const el of descendants(root)) if (el.hasAttribute('id')) ids.add(el.getAttribute('id'));
  return ids;
}

/** 비어 있는 다음 id(`f1`, `f2`…). 기존 id(사람·모델이 붙인 것 포함)와 절대 겹치지 않는다. */
export function allocId(root, used = usedIds(root)) {
  let n = 1;
  while (used.has(`f${n}`)) n += 1;
  const id = `f${n}`;
  used.add(id);
  return id;
}

export function make(tag, attrs = {}) {
  const el = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) if (v !== null && v !== undefined) el.setAttribute(k, String(v));
  return el;
}

/** 화살촉 marker 를 defs 에 보장하고 id 를 돌려준다. defs 가 없으면 루트 첫 자식으로 만든다(misplaced_defs 방지). */
export function ensureArrowMarker(root) {
  const all = descendants(root);
  const existing = all.find((el) => el.localName === 'marker' && el.getAttribute('id') === ARROW_MARKER_ID);
  if (existing) return ARROW_MARKER_ID;
  const used = usedIds(root);
  let id = ARROW_MARKER_ID;
  for (let n = 2; used.has(id); n += 1) id = `${ARROW_MARKER_ID}-${n}`;   // 다른 종류의 요소가 그 id 를 쓰는 드문 경우
  let defs = elementChildren(root).find((el) => el.localName === 'defs');
  if (!defs) {
    defs = make('defs');
    root.insertBefore(defs, root.firstChild);
  }
  const marker = make('marker', {
    id, viewBox: '0 0 10 10', refX: 9, refY: 5, markerWidth: 6, markerHeight: 6, orient: 'auto-start-reverse',
  });
  marker.appendChild(make('path', { d: 'M 0 0 L 10 5 L 0 10 z', fill: 'currentColor' }));
  defs.appendChild(marker);
  return id;
}

/** 새 도형의 선 굵기 — 그림에 있는 stroke-width 의 중앙값, 없으면 viewBox 폭의 1/400(최소 0.5). */
export function defaultStrokeWidth(root) {
  const widths = [];
  for (const { el } of listSelectable(root)) {
    const w = Number(el.getAttribute('stroke-width'));
    if (el.hasAttribute('stroke-width') && Number.isFinite(w) && w > 0) widths.push(w);
  }
  if (widths.length) {
    widths.sort((a, b) => a - b);
    return widths[Math.floor(widths.length / 2)];
  }
  return Math.max(0.5, Number((viewBoxOf(root)[2] / 400).toFixed(2)));
}

export const pointsAttr = (points) => points.map((p) => `${fmt(p.x)},${fmt(p.y)}`).join(' ');
export const pathData = (points) => points.map((p, i) => `${i ? 'L' : 'M'} ${fmt(p.x)} ${fmt(p.y)}`).join(' ');

function perpDist(p, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (!len2) return Math.hypot(p.x - a.x, p.y - a.y);
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/** 자유곡선 점 단순화(Ramer–Douglas–Peucker). tolerance 는 사용자 좌표 단위. */
export function simplify(points, tolerance) {
  if (points.length < 3) return points.slice();
  const first = points[0];
  const last = points[points.length - 1];
  let idx = -1;
  let max = 0;
  for (let i = 1; i < points.length - 1; i += 1) {
    const d = perpDist(points[i], first, last);
    if (d > max) { max = d; idx = i; }
  }
  if (max > tolerance) {
    return [...simplify(points.slice(0, idx + 1), tolerance).slice(0, -1), ...simplify(points.slice(idx), tolerance)];
  }
  return [first, last];
}

/** text 안의 글자 노드(text/tspan 직속)만 — 줄바꿈·탭뿐인 서식 노드는 뺀다(시제품 textNodes). */
export function textRuns(el) {
  const walker = document.createTreeWalker(el, 4 /* NodeFilter.SHOW_TEXT */);
  const out = [];
  while (walker.nextNode()) if (!/^[\r\n\t]+$/.test(walker.currentNode.nodeValue)) out.push(walker.currentNode);
  return out;
}

/** 자기 또는 조상(루트까지)의 속성값 — 상속 표현 속성을 인스펙터에 보이기 위해. 없으면 ''. */
export function inheritedAttr(el, root, name) {
  for (let n = el; n; n = n.parentNode) {
    if (n.nodeType !== ELEMENT_NODE) break;
    if (n.hasAttribute(name)) return n.getAttribute(name);
    if (n === root) break;
  }
  return '';
}

/** 점선 프리셋 → stroke-dasharray 값(선 굵기 배수). solid 는 'none'. */
export function dasharrayFor(presetId, strokeWidth) {
  const preset = DASH_PRESETS.find((p) => p.id === presetId);
  if (!preset || !preset.pattern) return 'none';
  const unit = Number(strokeWidth) > 0 ? Number(strokeWidth) : 1;
  return preset.pattern.map((k) => fmt(k * unit)).join(' ');
}

/** stroke-dasharray 값 → 프리셋 id('custom' 은 프리셋 밖의 값). */
export function presetOf(value, strokeWidth) {
  const v = (value || '').trim();
  if (!v || v === 'none') return 'solid';
  const norm = v.split(/[\s,]+/).map((t) => fmt(Number(t))).join(' ');
  const hit = DASH_PRESETS.find((p) => p.pattern && dasharrayFor(p.id, strokeWidth) === norm);
  return hit ? hit.id : 'custom';
}

/** 깊은 복제 + 안에 있는 id 전부 새 id 로(duplicate_id 방지). url(#…) 참조는 원래 정의(defs)를 그대로 가리킨다. */
export function cloneWithFreshIds(root, el) {
  const clone = el.cloneNode(true);
  const used = usedIds(root);
  for (const node of descendants(clone)) if (node.hasAttribute('id')) node.setAttribute('id', allocId(root, used));
  return clone;
}

/** 요소를 떠받치는 루트 안의 그리기 자식 중 첫 자리(defs 뒤) — 「맨 뒤로」가 defs 앞에 끼지 않게. */
export function firstDrawable(parent) {
  return elementChildren(parent).find((c) => c.localName !== 'defs') || null;
}
